import { useEffect, useRef } from "react";
import { useQueryParams } from "@tecnobit-srl/hook-use-query-params";
import { useShowToast } from "@tecnobit-srl/toasts";
import { useTranslation } from "react-i18next";
//#region src/registry.ts
/**
* The `?alert=<key>` values worth reacting to.
*
* A registry rather than a naked lookup because the key arrives in the URL: an
* unlisted value is ignored, so nobody can put raw translation keys — or a
* missing-key echo of their own text — on the page with a crafted link.
*/
var ALERTS = {
	confirm_consultancy: {
		type: "success",
		duration: 1e4,
		titleKey: "alerts.confirm_consultancy.title",
		messageKey: "alerts.confirm_consultancy.message"
	},
	consultancy_confirmed: {
		type: "success",
		duration: 1e4,
		titleKey: "alerts.consultancy_confirmed.title",
		messageKey: "alerts.consultancy_confirmed.message"
	},
	login_success: {
		type: "success",
		duration: 5e3,
		titleKey: "alerts.login_success.title",
		messageKey: "alerts.login_success.message"
	},
	login_error: {
		type: "error",
		duration: 1e4,
		titleKey: "alerts.login_error.title",
		messageKey: "alerts.login_error.message"
	},
	logout_success: {
		type: "success",
		duration: 5e3,
		titleKey: "alerts.logout_success.title",
		messageKey: "alerts.logout_success.message"
	}
};
//#endregion
//#region src/alerts.tsx
/**
* Turns an `?alert=<key>` query parameter into a toast.
*
* It exists for arrivals the app cannot otherwise detect: a visitor coming back
* from an external hop — paying for a consultancy on the e-shop, say — lands on a
* page that has no idea what just happened, and the parameter is the only thing
* carried across. Because the landing page is whatever an editor configured, this
* has to live in the app shell rather than in the feature that raised the flag.
*
* Renders nothing: the toast is pushed from an effect, which also keeps it out of
* the SSR output, so a cached page cannot end up with a confirmation baked in.
*/
function Alerts() {
	const { alert } = useQueryParams();
	const { t } = useTranslation("core");
	const showToast = useShowToast();
	const handled = useRef(null);
	useEffect(() => {
		if (!alert || handled.current === alert) return;
		const definition = ALERTS[alert];
		if (!definition) return;
		handled.current = alert;
		showToast({
			type: definition.type,
			duration: definition.duration,
			title: t(definition.titleKey),
			message: t(definition.messageKey)
		});
		const url = new URL(window.location.href);
		url.searchParams.delete("alert");
		window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
	}, [
		alert,
		showToast,
		t
	]);
	return null;
}
//#endregion
export { ALERTS, Alerts };
